import { useNavigate, useParams } from "@tanstack/react-router";

import {
  useUpdateWaterSource,
  useWaterSource,
} from "@/lib/api/water-sources";
import { ApiError } from "@/lib/api/client";
import { ErrorState } from "@/components/error-state";
import { OptimisticLockBanner } from "@/components/optimistic-lock-banner";

import {
  WaterSourceForm,
  emptyToUndef,
  parseOptNum,
  type WaterSourceFormValues,
} from "./form";

export function WaterSourceEditPage() {
  const { waterSourceId } = useParams({
    from: "/chemistry-profile/water-sources/$waterSourceId/edit",
  });
  const navigate = useNavigate();
  const q = useWaterSource(waterSourceId);
  const update = useUpdateWaterSource(waterSourceId);

  if (q.isLoading)
    return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (q.error) return <ErrorState error={q.error} retry={q.refetch} />;
  if (!q.data) return null;

  function submit(values: WaterSourceFormValues) {
    if (!q.data) return;
    update.mutate(
      {
        expected_updated_at: q.data.updated_at,
        name: values.name,
        baseline_ph: parseOptNum(values.baseline_ph),
        baseline_ec: parseOptNum(values.baseline_ec),
        alkalinity_estimate: parseOptNum(values.alkalinity_estimate),
        notes: emptyToUndef(values.notes),
        reason: emptyToUndef(values.reason),
      },
      {
        onSuccess: () => navigate({ to: "/chemistry-profile" }),
      },
    );
  }

  const stale = update.error instanceof ApiError && update.error.isStale;
  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold tracking-tight">
        Edit water source
      </h1>
      <WaterSourceForm
        initial={q.data}
        submitting={update.isPending}
        showReason
        submitLabel="Save changes"
        onSubmit={submit}
        banner={
          stale ? (
            <OptimisticLockBanner
              error={update.error}
              onRefresh={() => {
                update.reset();
                void q.refetch();
              }}
            />
          ) : undefined
        }
      />
      {update.error && !stale && (
        <ErrorState error={update.error} retry={() => update.reset()} />
      )}
    </div>
  );
}
